/**
 * KMMO kit additions for the Altar Forge (kept apart from the kit's own files): a range slider,
 * rarity odds rows, small slot marks (favored star, upgrade arrow), and the engraved altar ring
 * with node links behind the craft graph (mockup docs/art/mockups/forge.jpg).
 * Styles: ./extra-forge.css (prefix km-xf-), sizes in `--ku` like the rest of the kit.
 */
import { el, svg } from '../ui/dom';
import { type KmRarity, clamp01 } from './helpers';
import './extra-forge.css';

/** Thin gold-thumbed range slider ("Add Venom" 3/10). */
export function rangeSlider(o: { value: number; min: number; max: number; disabled?: boolean; label?: string; onChange?: (v: number) => void; onInput?: (v: number) => void }, parent?: HTMLElement): { el: HTMLElement; set(v: number, max?: number): void } {
  const root = el('div', 'km-xf-range', parent);
  const input = el('input', 'km-xf-range-input', root);
  input.type = 'range';
  input.step = '1';
  if (o.label) input.setAttribute('aria-label', o.label);
  const out = el('span', 'km-xf-range-out km-num', root);
  let max = o.max;
  const set = (v: number, m?: number): void => {
    if (m !== undefined) max = m;
    input.min = String(o.min);
    input.max = String(Math.max(o.min, max));
    input.value = String(v);
    const f = max > o.min ? clamp01((v - o.min) / (max - o.min)) : 0;
    root.style.setProperty('--f', `${(f * 100).toFixed(1)}%`);
    out.textContent = `${v}/${max}`;
  };
  set(o.value);
  input.disabled = !!o.disabled;
  input.addEventListener('input', () => {
    set(Number(input.value));
    o.onInput?.(Number(input.value));
  });
  input.addEventListener('change', () => o.onChange?.(Number(input.value)));
  return { el: root, set };
}

/** One odds row: rarity-coloured name, a bar in that colour, the percent. */
export function rarityBar(rarity: KmRarity, label: string, pct: number, pctText: string, parent?: HTMLElement): HTMLElement {
  const row = el('div', 'km-xf-odds', parent);
  row.dataset['rarity'] = rarity;
  el('span', 'km-xf-odds-name', row, label);
  const track = el('div', 'km-xf-odds-track', row);
  const fill = el('div', 'km-xf-odds-fill', track);
  fill.style.width = `${Math.max(pct > 0 ? 1.5 : 0, Math.min(100, pct))}%`;
  el('span', 'km-xf-odds-pct km-num', row, pctText);
  return row;
}

/** Small mark on a slot corner: a gold star (favored today) or a green ▲ (upgrade). */
export function slotMark(slot: HTMLElement, kind: 'star' | 'up', title?: string): HTMLElement {
  const m = el('span', `km-xf-mark km-xf-mark-${kind}`, slot, kind === 'star' ? '★' : '▲');
  if (title) m.title = title;
  return m;
}

/**
 * Engraved altar ring (thin gold circles, compass diamonds, faint rune ticks), drawn in a
 * 100×100 viewBox that stretches over its host. `links` are node centres (in %) joined to the
 * centre with thin gold lines.
 */
export function altarRing(links: readonly { x: number; y: number }[], parent?: HTMLElement): SVGSVGElement {
  const ticks: string[] = [];
  for (let i = 0; i < 48; i++) {
    const a = (i / 48) * Math.PI * 2;
    const r0 = i % 4 === 0 ? 44 : 45.2;
    ticks.push(`<line x1="${(50 + Math.cos(a) * r0).toFixed(2)}" y1="${(50 + Math.sin(a) * r0).toFixed(2)}" x2="${(50 + Math.cos(a) * 46.5).toFixed(2)}" y2="${(50 + Math.sin(a) * 46.5).toFixed(2)}"/>`);
  }
  const diamonds = [
    [50, 2],
    [98, 50],
    [50, 98],
    [2, 50],
  ]
    .map(([x, y]) => `<path class="km-xf-ring-dia" d="M${x} ${y! - 1.6} L${x! + 1.6} ${y} L${x} ${y! + 1.6} L${x! - 1.6} ${y}Z"/>`)
    .join('');
  const lines = links.map((p) => `<line class="km-xf-ring-link" x1="50" y1="50" x2="${p.x.toFixed(2)}" y2="${p.y.toFixed(2)}"/>`).join('');
  const s = svg(`<svg class="km-xf-ring" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
    <circle class="km-xf-ring-c outer" cx="50" cy="50" r="48"/>
    <circle class="km-xf-ring-c" cx="50" cy="50" r="46.5"/>
    <circle class="km-xf-ring-c faint" cx="50" cy="50" r="34"/>
    <circle class="km-xf-ring-c faint" cx="50" cy="50" r="22"/>
    <g class="km-xf-ring-ticks">${ticks.join('')}</g>
    <path class="km-xf-ring-c faint" d="M50 4 L50 96 M4 50 L96 50"/>
    ${lines}
    ${diamonds}
  </svg>`);
  if (parent) parent.appendChild(s);
  return s;
}
